import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { Save, X, Users } from 'lucide-react';
import axios from 'axios';
import toast from 'react-hot-toast';

const EditCrew = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue
  } = useForm();

  // Helper function to get auth headers
  const getAuthHeaders = () => {
    const token = localStorage.getItem('authToken');
    return {
      headers: {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      }
    };
  };

  const fetchCrew = useCallback(async () => {
    try {
      setInitialLoading(true);
      const response = await axios.get(`/api/crew/${id}`, getAuthHeaders());
      const crew = response.data;
      // Set all form values
      Object.keys(crew).forEach(key => {
        if (key === 'is_active' || key === 'is_me' || key === 'pic' || key === 'sic') {
          setValue(key, Boolean(crew[key]));
        } else if (key === 'medical_expiry') {
          // Format date for input field
          setValue(key, crew[key] ? crew[key].split('T')[0] : '');
        } else {
          setValue(key, crew[key] || '');
        }
      });
    } catch (error) {
      console.error('Error fetching crew member:', error);
      toast.error('Failed to load crew member data');
      navigate('/crew');
    } finally {
      setInitialLoading(false);
    }
  }, [id, setValue, navigate]);

  useEffect(() => {
    fetchCrew();
  }, [fetchCrew]);

  const onSubmit = async (data) => {
    try {
      setLoading(true);
      await axios.put(`/api/crew/${id}`, data, getAuthHeaders());
      toast.success('Crew member updated successfully!');
      navigate('/crew');
    } catch (error) {
      console.error('Error updating crew member:', error);
      toast.error('Failed to update crew member');
    } finally {
      setLoading(false);
    }
  };

  if (initialLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-aviation-600"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center space-x-3 mb-4">
          <div className="aviation-gradient p-2 rounded-lg">
            <Users className="h-6 w-6 text-white" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900">Edit Crew Member</h1>
        </div>
        <p className="text-gray-600">
          Update crew member information and certifications
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
        {/* Basic Information */}
        <div className="card">
          <h2 className="text-xl font-bold text-gray-900 mb-6">Basic Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Full Name *
              </label>
              <input
                type="text"
                {...register('name', { required: 'Name is required' })}
                className="input-field"
                placeholder="e.g., John Smith"
              />
              {errors.name && (
                <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Email Address
              </label>
              <input
                type="email"
                {...register('email')}
                className="input-field"
                placeholder="john.smith@example.com"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Phone Number
              </label>
              <input
                type="tel"
                {...register('phone')}
                className="input-field"
                placeholder="(555) 123-4567"
              />
            </div>
                      
            <div className="md:col-span-2">
              <div className="flex flex-wrap items-center gap-6">
                <label className="flex items-center space-x-3">
                  <span className="text-sm font-medium text-gray-700">This Is Me</span>
                  <div className="relative inline-block w-16 h-8">
                    <input
                      type="checkbox"
                      {...register('is_me')}
                      className="sr-only peer"
                    />
                    <div className="w-16 h-8 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-aviation-300 rounded-full peer peer-checked:after:translate-x-8 peer-checked:after:border-white after:content-[''] after:absolute after:top-1 after:left-1 after:bg-white after:border-gray-300 after:border after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-aviation-600"></div>
                  </div>
                </label>

                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    {...register('pic')}
                    className="h-4 w-4 text-aviation-600 focus:ring-aviation-500 border-gray-300 rounded"
                  />
                  <label className="text-sm font-medium text-gray-700">
                    Default PIC
                  </label>
                </div>

                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    {...register('sic')}
                    className="h-4 w-4 text-aviation-600 focus:ring-aviation-500 border-gray-300 rounded"
                  />
                  <label className="text-sm font-medium text-gray-700">
                    Default SIC
                  </label>
                </div>
              </div>
              <p className="mt-2 text-xs text-gray-500">
                Default roles determine which crew member is automatically selected for new flights
              </p>
            </div>
          </div>
        </div>

        {/* Certification Information */}
        <div className="card">
          <h2 className="text-xl font-bold text-gray-900 mb-6">Pilot Certification</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Certificate Number
              </label>
              <input
                type="text"
                {...register('certificate_number')}
                className="input-field"
                placeholder="e.g., 123456789"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Certificate Type
              </label>
              <select {...register('certificate_type')} className="input-field">
                <option value="student">Student Pilot</option>
                <option value="sport">Sport Pilot</option>
                <option value="recreational">Recreational Pilot</option>
                <option value="private">Private Pilot</option>
                <option value="commercial">Commercial Pilot</option>
                <option value="airline_transport">Airline Transport Pilot (ATP)</option>
                <option value="flight_instructor">Flight Instructor (CFI)</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Medical Class
              </label>
              <select {...register('medical_class')} className="input-field">
                <option value="first">First Class</option>
                <option value="second">Second Class</option>
                <option value="third">Third Class</option>
                <option value="basic_med">BasicMed</option>
                <option value="sport">Sport Pilot (No Medical)</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Medical Expiry Date
              </label>
              <input
                type="date"
                {...register('medical_expiry')}
                className="input-field"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Total Experience Hours
              </label>
              <input
                type="number"
                step="0.1"
                {...register('experience_hours', {
                  min: { value: 0, message: 'Experience hours cannot be negative' }
                })}
                className="input-field"
                placeholder="e.g., 1500.5"
              />
              {errors.experience_hours && (
                <p className="mt-1 text-sm text-red-600">{errors.experience_hours.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Ratings (comma-separated)
              </label>
              <input
                type="text"
                {...register('ratings')}
                className="input-field"
                placeholder="e.g., Instrument, Multi-Engine, CFI"
              />
              <p className="mt-1 text-xs text-gray-500">
                Separate multiple ratings with commas
              </p>
            </div>
          </div>
        </div>

        {/* Employment Information */}
        <div className="card">
          <h2 className="text-xl font-bold text-gray-900 mb-6">Employment Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Company/Organization
              </label>
              <input
                type="text"
                {...register('company')}
                className="input-field"
                placeholder="e.g., Delta Airlines, Local Flight School"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Position/Role
              </label>
              <input
                type="text"
                {...register('position')}
                className="input-field"
                placeholder="e.g., Captain, First Officer, Flight Instructor"
              />
            </div>
          </div>
        </div>

        {/* Additional Information */}
        <div className="card">
          <h2 className="text-xl font-bold text-gray-900 mb-6">Additional Information</h2>
          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Notes
              </label>
              <textarea
                {...register('notes')}
                rows={4}
                className="input-field"
                placeholder="Additional notes about this crew member, specialties, preferences, etc."
              />
            </div>

            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                {...register('is_active')}
                className="h-4 w-4 text-aviation-600 focus:ring-aviation-500 border-gray-300 rounded"
              />
              <label className="text-sm text-gray-900">
                Active crew member (uncheck to deactivate without deleting)
              </label>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end space-x-4">
          <button
            type="button"
            onClick={() => navigate('/crew')}
            className="btn-secondary flex items-center space-x-2"
          >
            <X className="h-4 w-4" />
            <span>Cancel</span>
          </button>
          <button
            type="submit"
            disabled={loading}
            className="btn-primary flex items-center space-x-2 disabled:opacity-50"
          >
            {loading ? (
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
            ) : (
              <Save className="h-4 w-4" />
            )}
            <span>{loading ? 'Updating...' : 'Update Crew Member'}</span>
          </button>
        </div>
      </form>
    </div>
  );
};

export default EditCrew;